import { useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ChevronDown } from 'lucide-react';

/** SEO + metadata fields shared by the new/edit post editors. */
export interface SeoFields {
  seo_title: string;
  seo_description: string;
  excerpt: string;
  noindex: boolean;
}

export const emptySeoFields: SeoFields = {
  seo_title: '',
  seo_description: '',
  excerpt: '',
  noindex: false,
};

/** Recommended (soft) lengths — hints only, not enforced. */
const SEO_TITLE_MAX = 60;
const SEO_DESC_MAX = 160;

interface PostSeoPanelProps {
  value: SeoFields;
  onChange: (patch: Partial<SeoFields>) => void;
}

/**
 * Collapsible SEO & metadata section for the post editor: excerpt, SEO
 * title/description (with length hints), and a noindex toggle. The feature
 * image lives in the adjacent FeatureImageCard.
 */
export function PostSeoPanel({ value, onChange }: PostSeoPanelProps) {
  const [open, setOpen] = useState(false);
  const autoExpanded = useRef(false);

  // Auto-expand once if the post already has SEO metadata (e.g. set via MCP),
  // mirroring how the page editor reveals existing custom CSS/JS on load.
  useEffect(() => {
    if (
      !autoExpanded.current &&
      (value.seo_title || value.seo_description || value.excerpt || value.noindex)
    ) {
      setOpen(true);
      autoExpanded.current = true;
    }
  }, [value]);

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 font-medium text-sm hover:text-foreground/80"
      >
        <ChevronDown className={`h-4 w-4 transition-transform ease-out ${open ? 'rotate-180' : ''}`} />
        SEO &amp; Metadata
      </button>

      {open && (
        <div className="disclosure-reveal space-y-4 pl-6 border-l-2 border-muted">
          {/* Excerpt */}
          <div className="space-y-2">
            <Label htmlFor="excerpt">Excerpt</Label>
            <Textarea
              id="excerpt"
              value={value.excerpt}
              onChange={(e) => onChange({ excerpt: e.target.value })}
              placeholder="Short summary used in listings, RSS, and social cards."
              rows={2}
            />
          </div>

          {/* SEO title */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="seo-title">SEO title</Label>
              <span className="text-xs text-muted-foreground">
                {value.seo_title.length} / ~{SEO_TITLE_MAX}
              </span>
            </div>
            <Input
              id="seo-title"
              value={value.seo_title}
              onChange={(e) => onChange({ seo_title: e.target.value })}
              placeholder="Defaults to the post title"
            />
          </div>

          {/* SEO description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="seo-description">Meta description</Label>
              <span className="text-xs text-muted-foreground">
                {value.seo_description.length} / ~{SEO_DESC_MAX}
              </span>
            </div>
            <Textarea
              id="seo-description"
              value={value.seo_description}
              onChange={(e) => onChange({ seo_description: e.target.value })}
              placeholder="Shown in search results and social previews."
              rows={3}
            />
          </div>

          {/* noindex */}
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label>Hide from search engines</Label>
              <p className="text-xs text-muted-foreground">
                Adds a noindex tag so search engines skip this post.
              </p>
            </div>
            <Switch
              checked={value.noindex}
              onCheckedChange={(checked) => onChange({ noindex: checked })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
